import { ReactNode } from 'react';
import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import { HeroContainer } from '@fe-learner-common/components/HeroContainer';
import { InstructorsPromo } from '@fe-learner-common/components/InstructorsPromo';
import { RichText } from '@fe-core-ui/src/components/RichText';
import { isProjectSelection } from '@fe-learner-common/api/schemas/sessionsSchema';
import { TSelfregSessionDetails } from '@api/io/selfreg.io';
import { AuthorizationActions } from '@routes/selfreg/components/AuthorizationActions';
import { useSelfregEnrollmentActions } from '@routes/selfreg/hooks/useEnrollmentActions';
import { FaIcon, Tag } from '@fe-learner-common';
import { useSelfregSessionDetailsTags } from '@routes/selfreg/hooks/useSelfregSessionDetailsTags';
import { AutoEnrollment } from '@routes/selfreg/components/AutoEnrollment';

export const SelfRegPage = ({
  sessionDetails,
  children,
}: {
  sessionDetails: TSelfregSessionDetails;
  children: ReactNode;
}) => {
  const intl = useIntl();

  const enrollmentActions = useSelfregEnrollmentActions({ sessionDetails });
  const tags = useSelfregSessionDetailsTags(sessionDetails);
  const isSelection = isProjectSelection(sessionDetails);

  return (
    <div>
      <AutoEnrollment
        sessionId={sessionDetails.id}
        courseName={sessionDetails?.name}
      />
      <HeroContainer
        courseImage={sessionDetails.thumbnailUrl}
        courseImageAlt={intl.formatMessage({
          id: 'learner.common.course.image',
        })}
        background={{
          backdrop: sessionDetails.thumbnailUrl,
          image: sessionDetails?.bgImage,
          color: sessionDetails?.bgColor,
        }}
      >
        <div>
          <h1 className="lf:text-headline-sm">{sessionDetails?.name}</h1>
          <div className="lf:flex lf:flex-wrap lf:gap-3 lf:pt-2">
            {tags
              ? Object.entries(tags).map(([key, tag]) =>
                  tag ? (
                    <Tag
                      key={key}
                      type={tag.type}
                      className="lf:whitespace-nowrap"
                    >
                      <FaIcon width={12} icon={tag.icon} />
                      {tag.text}
                    </Tag>
                  ) : null,
                )
              : null}
          </div>
        </div>
        {isSelection && sessionDetails.description ? (
          <RichText className="lf:text-body-md lf:text-neutral-500">
            {sessionDetails.description}
          </RichText>
        ) : null}
        <div className="lf:flex lf:flex-wrap lf:gap-5">
          <AuthorizationActions sessionId={sessionDetails.id} />
          {enrollmentActions?.enrollmentAction}
          {!isSelection && sessionDetails.trainers?.length ? (
            <Link to="./instructors">
              <InstructorsPromo
                visiblePersons={sessionDetails.trainers
                  .slice(0, 3)
                  .map((trainer) => ({
                    name: trainer.name || '',
                    thumbnail: trainer.thumbnail || '',
                  }))}
                totalPersons={sessionDetails.trainers?.length}
              />
            </Link>
          ) : null}
        </div>
      </HeroContainer>
      {children}
    </div>
  );
};
